package com.htmlagent.site.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.until

/**
 * The commission assistant: a dialog over the workspace, and the controls inside it.
 *
 * Every value comes from [Tokens], like the rest of the interface. What is particular here is
 * the selected state: it is expressed as `[data-on="true"]` on the same class rather than as a
 * second class, because two classes setting the same property tie on specificity and the winner
 * is whichever Kobweb registered last — an order nobody controls. A suffix rule wins by
 * specificity, which is a rule and not luck.
 */
private val FADE_BACKGROUND_AND_BORDER =
    "background ${Motion.Base} ${Motion.Ease}, border-color ${Motion.Base} ${Motion.Ease}"

/**
 * What covers the workspace behind the dialog: a tint that fades in, and a blur that waits.
 *
 * The blur is a layer of its own that switches on once the dialog has risen. Present from the
 * first frame, every frame of the entrance re-ran it over the whole workspace, iframe included:
 * measured on a desktop, 6 to 9 frames lost in the entrance with it, 3 to 4 without. Animating
 * the blur itself cost more, 13.
 */
val BriefScrimStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "fixed")
            property("inset", "0")
            property("z-index", "${Layer.Popover}")
            // Keeps the blur layer's `z-index: -1` inside the scrim, under the dialog.
            property("isolation", "isolate")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("padding", "${Tokens.spaceXl.value()}")
            property("background", Tokens.modalScrim.value())
            property("animation", "ha-brief-in ${Motion.Base} ${Motion.Ease} backwards")
        }
    }
    cssRule("::before") {
        Modifier.styleModifier {
            property("content", "\"\"")
            property("position", "absolute")
            property("inset", "0")
            property("z-index", "-1")
            property("backdrop-filter", "blur(3px) saturate(120%)")
            property("-webkit-backdrop-filter", "blur(3px) saturate(120%)")
            property("pointer-events", "none")
            // Hidden for as long as the dialog takes to rise, then on in a single step.
            property("animation", "ha-brief-in 1ms ${Motion.Slow} backwards")
        }
    }
    // 24px on each side is 48 of the 390 a phone has, and they come off the column the
    // questions are read in. The frame is what a narrow screen gives up first.
    until(Breakpoints.Md) { Modifier.styleModifier { property("padding", "0") } }
}

/**
 * The dialog itself: as wide as the palette needs and never taller than the window.
 *
 * Its width changes at once when the brief opens beside the questions. Over the blurred scrim,
 * a transition of the whole dialog lost 4 to 9 of its 11 frames, which is a slide in visible
 * steps; at once it loses none and reads as the layout it is.
 */
val BriefDialogStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.surface.value())
            .color(Tokens.fg.value())
            .styleModifier {
                // The questions alone. The second column only exists once it is asked for.
                property("width", "min(680px, 100%)")
                property("max-height", "min(760px, 100%)")
                property("display", "flex")
                property("flex-direction", "column")
                property("overflow", "hidden")
                property("border", "1px solid ${Tokens.line.value()}")
                property("border-radius", "${Tokens.radiusLg.value()}")
                property("box-shadow", "0 32px 80px rgba(0, 0, 0, 0.45)")
            }
    }
    // A sheet, not a card: with the scrim's frame gone there is nothing for a rounded corner to
    // sit against, and the 112px the fixed height was leaving unused are a question and a half.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("height", "100%")
            property("max-height", "100%")
            property("border", "none")
            property("border-radius", "0")
        }
    }
    // A suffix rule on the same class, not a second class: two classes setting `width` tie and
    // the winner is whichever Kobweb registered last.
    cssRule("[data-expert=\"true\"]") {
        Modifier.styleModifier { property("width", "min(936px, 100%)") }
    }
}

/** The quiet control at the foot of the questions that unfolds the brief and its craft notes. */
val BriefDisclosureStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("align-self", "flex-start")
            property("margin-top", "auto")
            property("padding", "0")
            property("background", "transparent")
            property("border", "none")
            property("font-size", "${Tokens.textXs.value()}")
            property("text-decoration", "underline")
            property("text-underline-offset", "3px")
            property("cursor", "pointer")
        }
    }
    hover { Modifier.color(Tokens.fg.value()) }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
}

/** Title, step rail and the way out, in one row. */
val BriefHeadStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("flex", "none")
            property("display", "flex")
            property("align-items", "center")
            // The row wraps rather than crushing the subtitle into a five-line column. Folded
            // away, the panel takes 256 px off the dialog and this row is the first thing to
            // feel it.
            property("flex-wrap", "wrap")
            property("gap", "${Tokens.spaceLg.value()}")
            property("padding", "${Tokens.spaceLg.value()} ${Tokens.spaceXl.value()}")
            property("border-bottom", "1px solid ${Tokens.line.value()}")
        }
    }
    // Measured: the four steps come to 355px in Spanish, and 24px of frame on each side puts
    // them at 403 - past a 390px phone before a single letter of the title. The frame gives way
    // first, and [BriefStepsStyle] wraps for whatever is still too wide.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("position", "relative")
            // Room on the right for the close button, which is out of the flow at this width.
            // The sheet reaches the top edge, and in the installed app that is under the camera.
            property(
                "padding",
                "calc(${Tokens.spaceLg.value()} + $SAFE_AREA_TOP) 48px " +
                    "${Tokens.spaceLg.value()} ${Tokens.spaceLg.value()}",
            )
        }
    }
}

/**
 * The four questions as a row of jump targets.
 *
 * Pushed to the end of the head while there is room for it there, and on its own line, wrapping,
 * once there is not: they are the map of the assistant, and a map that runs off the screen is
 * worse than one on two lines.
 */
val BriefStepsStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "flex")
            property("gap", "4px")
            property("margin-left", "auto")
        }
    }
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("flex-wrap", "wrap")
            property("margin-left", "0")
        }
    }
}

/** The title and its lede, which need room before anything else in the row gives way. */
val BriefHeadTextStyle = CssStyle.base {
    Modifier.styleModifier {
        property("flex", "1 1 260px")
        property("min-width", "0")
    }
}

/** The tinted square that says this is the assistant and not the page. */
val BriefBadgeStyle = CssStyle.base {
    Modifier
        .backgroundColor(Tokens.accentSurface.value())
        .color(Tokens.accent.value())
        .styleModifier {
            property("width", "34px")
            property("height", "34px")
            property("flex", "none")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("border", "1px solid ${Tokens.accentLine.value()}")
            property("border-radius", "${Tokens.radiusSm.value()}")
        }
}

/** One of the four questions, as a jump target. */
val BriefStepStyle = CssStyle {
    base {
        Modifier
            .fontSize(Tokens.textXs.value())
            .color(Tokens.muted.value())
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "${Tokens.spaceXs.value()}")
                property("height", "${Tokens.controlSm.value()}")
                property("padding", "0 12px")
                property("border", "1px solid transparent")
                property("border-radius", "${Tokens.radiusFull.value()}")
                property("background", "transparent")
                property("font-family", "inherit")
                property("font-weight", "${Weight.Semibold}")
                property("cursor", "pointer")
                property("white-space", "nowrap")
                property("transition", "background ${Motion.Base} ${Motion.Ease}, color ${Motion.Base} ${Motion.Ease}")
            }
    }
    hover { Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.accentWash.value()) } }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    // Measured on a phone: the four steps came to 343px against a 329px row and "Forma" went
    // to a line of its own. Four pixels less on each side of each pill puts them at 311.
    until(Breakpoints.Md) { Modifier.styleModifier { property("padding", "0 8px") } }
    cssRule("[data-on=\"true\"]") {
        Modifier
            .color(Tokens.fg.value())
            .backgroundColor(Tokens.accentSurface.value())
            .styleModifier { property("border-color", Tokens.accent.value().toString()) }
    }
}

/** The number inside a step pill, which is what makes the rail read as a sequence. */
val BriefStepNumberStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("width", "18px")
            property("height", "18px")
            property("flex", "none")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("font-size", "10px")
            property("border-radius", "${Tokens.radiusFull.value()}")
            property("background", Tokens.raised.value().toString())
            property("color", Tokens.muted.value().toString())
        }
    }
    cssRule("[data-on=\"true\"]") {
        Modifier.styleModifier {
            property("background", Tokens.accent.value().toString())
            property("color", Tokens.onAccent.value().toString())
        }
    }
}

/** The question on the left, the brief being written on the right. */
val BriefBodyStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("flex", "1")
            property("display", "flex")
            property("min-height", "0")
        }
    }
    // On a phone the two sit one under the other and the body is the single scroller: two
    // columns of 576 and "whatever is left" put the brief in a 49px gutter, cut off outside
    // the dialog. The pane's fixed width is handed over as a custom property rather than
    // overridden, because it is set by `[data-expert="true"]` and a plain class cannot outrank
    // an attribute selector - it would tie on nothing and lose.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("flex-direction", "column")
            property("overflow-y", "auto")
            property(BRIEF_PANE_WIDTH, "100%")
        }
    }
}

/**
 * How wide the question column is when the brief is showing beside it.
 *
 * A custom property and not a value, so [BriefBodyStyle] can hand a narrow screen a different
 * one: what sets it is an attribute rule, and a media query on the plain class would lose to it
 * on specificity. Written by hand and read with `var()` because both ends are in this file.
 */
private const val BRIEF_PANE_WIDTH = "--ha-brief-pane-width"

/**
 * Where the current question lives.
 *
 * On its own it fills the dialog; beside the panel it goes back to its fixed column so the
 * brief keeps the width it was measured for. The state is a suffix rule on the same class for
 * the reason given at the top of this file.
 */
val BriefPaneStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("flex", "1 1 auto")
            property("min-width", "0")
            property("padding", "${Tokens.spaceXl.value()}")
            property("box-sizing", "border-box")
            property("overflow-y", "auto")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "${Tokens.spaceXl.value()}")
        }
    }
    cssRule("[data-expert=\"true\"]") {
        Modifier.styleModifier {
            property("width", "var($BRIEF_PANE_WIDTH, 576px)")
            property("flex", "none")
        }
    }
    // Stacked, each part is as tall as it is and the body scrolls past both. Left growing,
    // they would share one screen's height and each would scroll inside a half of it.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("flex", "none")
            property("overflow-y", "visible")
        }
    }
}

/** The accumulating brief: the same text that will travel, shown as it is written. */
val BriefSummaryStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.well.value())
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property("padding", "${Tokens.spaceXl.value()}")
                property("box-sizing", "border-box")
                property("overflow-y", "auto")
                property("border-left", "1px solid ${Tokens.line.value()}")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "${Tokens.spaceLg.value()}")
            }
    }
    // Below the question rather than beside it, so the line that separates them moves with it.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("flex", "none")
            property("overflow-y", "visible")
            property("border-left", "none")
            property("border-top", "1px solid ${Tokens.line.value()}")
        }
    }
}

/** The question itself. */
val BriefHeadingStyle = CssStyle.base {
    Modifier.fontSize(Tokens.textMd.value()).styleModifier {
        property("margin", "0")
        property("font-weight", "${Weight.Semibold}")
        property("line-height", Leading.Tight)
        property("letter-spacing", Tracking.Tight)
    }
}

/** The line under it, which says why the question is being asked. */
val BriefLedeStyle = CssStyle.base {
    Modifier.fontSize(Tokens.textSm.value()).color(Tokens.muted.value()).styleModifier {
        property("margin", "6px 0 0")
        property("line-height", Leading.Normal)
    }
}

/** A small monospace label over a group. */
val BriefEyebrowStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("font-family", Tokens.fontMono.value())
        property("font-size", "10px")
        property("font-weight", "${Weight.Semibold}")
        property("letter-spacing", Tracking.Eyebrow)
        property("text-transform", "uppercase")
    }
}

/** The label of a field or a group of controls. */
val BriefLabelStyle = CssStyle.base {
    Modifier.fontSize(Tokens.textXs.value()).color(Tokens.muted.value()).styleModifier {
        property("font-weight", "${Weight.Semibold}")
    }
}

/** A choice with a name and a line of explanation. */
val BriefCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.raised.value())
            .color(Tokens.fg.value())
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "6px")
                property("padding", "12px")
                property("height", "auto")
                property("width", "100%")
                property("min-width", "0")
                property("box-sizing", "border-box")
                property("text-align", "left")
                property("font-family", "inherit")
                property("cursor", "pointer")
                property("border", "1px solid ${Tokens.line.value()}")
                property("border-radius", "${Tokens.radiusMd.value()}")
                property("transition", FADE_BACKGROUND_AND_BORDER)
            }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.lineStrong.value().toString()) } }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    cssRule("[data-on=\"true\"]") {
        Modifier.backgroundColor(Tokens.accentSurface.value()).styleModifier {
            property("border-color", Tokens.accent.value().toString())
        }
    }
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
            property("gap", "6px")
            property("width", "100%")
            property("min-width", "0")
            // Silk's row keeps its children on one line; a card's second line is a sentence.
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
        }
    }
}

/** The name inside a card. */
val BriefCardNameStyle = CssStyle.base {
    Modifier.fontSize(Tokens.textSm.value()).styleModifier { property("font-weight", "${Weight.Semibold}") }
}

/** The line under it. */
val BriefCardLineStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("font-size", "11px")
        property("line-height", Leading.Snug)
    }
}

/** The marker that says a choice is the one you get without choosing. */
val BriefTagStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("font-family", Tokens.fontMono.value())
        property("font-size", "9px")
        property("letter-spacing", Tracking.Loose)
        property("padding", "1px 6px")
        property("white-space", "nowrap")
        property("border", "1px solid ${Tokens.lineStrong.value()}")
        property("border-radius", "${Tokens.radiusFull.value()}")
    }
}

/** A one-line choice: an action, a gesture. */
val BriefChipStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.raised.value())
            .color(Tokens.fg.value())
            .fontSize(Tokens.textXs.value())
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "6px")
                property("height", "${Tokens.controlSm.value()}")
                property("padding", "0 14px")
                property("font-family", "inherit")
                property("font-weight", "${Weight.Semibold}")
                property("cursor", "pointer")
                property("border", "1px solid ${Tokens.line.value()}")
                property("border-radius", "${Tokens.radiusFull.value()}")
                property("transition", FADE_BACKGROUND_AND_BORDER)
            }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.lineStrong.value().toString()) } }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    cssRule("[data-on=\"true\"]") {
        Modifier.backgroundColor(Tokens.accentSurface.value()).styleModifier {
            property("border-color", Tokens.accent.value().toString())
        }
    }
    // Out of budget rather than disabled: it can come back by dropping the other one.
    cssRule("[data-blocked=\"true\"]") {
        Modifier.styleModifier {
            property("opacity", "0.42")
            property("cursor", "not-allowed")
        }
    }
}

/** A text field or a text area. */
val BriefFieldStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.well.value())
            .color(Tokens.fg.value())
            .fontSize(Tokens.textSm.value())
            .styleModifier {
                property("width", "100%")
                property("box-sizing", "border-box")
                property("padding", "10px 12px")
                property("font-family", "inherit")
                property("line-height", Leading.Normal)
                property("resize", "none")
                property("border", "1px solid ${Tokens.line.value()}")
                property("border-radius", "${Tokens.radiusMd.value()}")
                property("transition", "border-color ${Motion.Base} ${Motion.Ease}")
            }
    }
    cssRule(":focus") {
        Modifier.styleModifier {
            property("outline", "none")
            property("border-color", Tokens.accent.value().toString())
            property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}")
        }
    }
}

/** The five numbers a register buys, side by side. */
val BriefBudgetStyle = CssStyle.base {
    Modifier.backgroundColor(Tokens.well.value()).styleModifier {
        property("display", "flex")
        property("gap", "${Tokens.spaceLg.value()}")
        property("padding", "12px 14px")
        property("border", "1px solid ${Tokens.line.value()}")
        property("border-radius", "${Tokens.radiusMd.value()}")
    }
}

/** The whole derived scale, as one strip. */
val BriefRampStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("overflow", "hidden")
        property("border-radius", "${Tokens.radiusSm.value()}")
    }
}

/**
 * The preview square, which is also where the accent is picked.
 *
 * The colour lives here and not on the `<input>` because a native colour input cannot be styled
 * the same way twice: its swatch is a vendor pseudo-element that Chrome and Firefox spell
 * differently, and a selector one of them does not know invalidates the rule. So the input goes
 * on top at zero opacity and this carries the whole appearance.
 */
val BriefColourWellStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("position", "relative")
            property("width", "76px")
            property("height", "76px")
            property("flex", "none")
            property("border-radius", "${Tokens.radiusMd.value()}")
            property("box-shadow", "inset 0 0 0 1px ${Tokens.ornament.value()}")
        }
    }
    cssRule(":has(input:focus-visible)") {
        Modifier.styleModifier {
            property("box-shadow", "inset 0 0 0 1px ${Tokens.ornament.value()}, 0 0 0 3px ${Tokens.focusRing.value()}")
        }
    }
}

/** The colour input itself: invisible, and the whole surface of the well is its hit area. */
val BriefColourPickerStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("inset", "0")
        property("width", "100%")
        property("height", "100%")
        property("padding", "0")
        property("opacity", "0")
        property("cursor", "pointer")
        property("border", "none")
        property("background", "none")
    }
}

/** One colour to start from. */
val BriefSwatchStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("width", "34px")
            property("height", "34px")
            property("padding", "0")
            property("cursor", "pointer")
            property("border", "2px solid transparent")
            property("border-radius", "${Tokens.radiusSm.value()}")
            property("box-shadow", "inset 0 0 0 1px ${Tokens.ornament.value()}")
            property("transition", "transform ${Motion.Base} ${Motion.Curve}")
        }
    }
    hover { Modifier.styleModifier { property("transform", "translateY(-2px)") } }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    cssRule("[data-on=\"true\"]") {
        Modifier.styleModifier { property("border-color", Tokens.fg.value().toString()) }
    }
}

/** A typeface pairing shown as itself, which is the only way to choose one. */
val BriefTypeRowStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.raised.value())
            .color(Tokens.fg.value())
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "${Tokens.spaceLg.value()}")
                property("width", "100%")
                property("box-sizing", "border-box")
                property("padding", "12px 14px")
                property("height", "auto")
                property("text-align", "left")
                property("font-family", "inherit")
                property("cursor", "pointer")
                property("border", "1px solid ${Tokens.line.value()}")
                property("border-radius", "${Tokens.radiusMd.value()}")
                property("transition", FADE_BACKGROUND_AND_BORDER)
            }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.lineStrong.value().toString()) } }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    cssRule("[data-on=\"true\"]") {
        Modifier.backgroundColor(Tokens.accentSurface.value()).styleModifier {
            property("border-color", Tokens.accent.value().toString())
        }
    }
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceLg.value()}")
            property("width", "100%")
        }
    }
}

/** A rule that is worth stating where the decision is taken, marked rather than boxed. */
val BriefNoteStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("font-size", "11px")
        property("line-height", Leading.Normal)
        property("padding-left", "10px")
        property("border-left", "2px solid ${Tokens.accentLine.value()}")
    }
}

/** A line of the brief. It is monospace because it is the text that will be sent. */
val BriefLineStyle = CssStyle.base {
    Modifier.color(Tokens.fg.value()).styleModifier {
        property("font-family", Tokens.fontMono.value())
        property("font-size", "11px")
        property("line-height", Leading.Relaxed)
        property("white-space", "pre-wrap")
        property("word-break", "break-word")
    }
}

/** The same, for what has been left for the agent to decide. */
val BriefLineOpenStyle = CssStyle.base {
    Modifier.color(Tokens.muted.value()).styleModifier {
        property("font-family", Tokens.fontMono.value())
        property("font-size", "11px")
        property("line-height", Leading.Relaxed)
        property("font-style", "italic")
    }
}

/** Skip, back and the way forward. */
val BriefFootStyle = CssStyle {
    base {
        Modifier.backgroundColor(Tokens.well.value()).styleModifier {
            property("flex", "none")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
            property("padding", "${Tokens.spaceLg.value()} ${Tokens.spaceXl.value()}")
            property("border-top", "1px solid ${Tokens.line.value()}")
        }
    }
    // The four of them do not fit across a phone, and a row that does not wrap does not stop:
    // the labels ran over each other, "Prefiero escribirlo yo" printed on top of "Saltar este
    // paso". Wrapped, the pair that moves the assistant forward stays on the last line.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("flex-wrap", "wrap")
            property("justify-content", "flex-end")
            property("row-gap", "${Tokens.spaceSm.value()}")
            property("padding", "${Tokens.spaceLg.value()} ${Tokens.spaceLg.value()}")
        }
    }
}

/** Three columns for six choices. */
val BriefGridStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "grid")
            property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
            property("gap", "${Tokens.spaceSm.value()}")
        }
    }
    // Measured on a 390px screen: three columns are 108px each, and "Landing" came out set one
    // letter per line with its tag hanging outside the card. Two columns give 167.
    until(Breakpoints.Md) {
        Modifier.styleModifier { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") }
    }
}

/**
 * The name of a choice and the tag that qualifies it, on one line while they fit.
 *
 * They wrap rather than compress: with no room, a flex row shrinks its children until the word
 * itself breaks, and a name set vertically is not a name any more.
 */
val BriefCardHeadStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("align-items", "center")
        property("flex-wrap", "wrap")
        property("gap", "6px")
    }
}

/** A row of chips that wraps rather than pushing its tail out of the dialog. */
val BriefWrapStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/** A column with a gap, which is most of the layout in here. */
val BriefStackStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Tokens.spaceSm.value()}")
    }
}

/**
 * The way into the assistant from an empty conversation, which is where it belongs.
 *
 * It sits under the empty state and disappears with it: the assistant produces a whole
 * commission, and a whole commission as a second message is a full rewrite rather than a
 * `replace_section`. Its moment is the first message, so its main door lives in the only
 * screen where there is nothing else to do.
 */
val BriefCallStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Tokens.accentSurface.value())
            .color(Tokens.fg.value())
            .fontSize(Tokens.textSm.value())
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "${Tokens.spaceSm.value()}")
                property("height", "auto")
                property("min-height", "${Tokens.controlLg.value()}")
                property("padding", "0 18px")
                property("font-family", "inherit")
                property("font-weight", "${Weight.Semibold}")
                property("cursor", "pointer")
                property("border", "1px solid ${Tokens.accentLine.value()}")
                property("border-radius", "${Tokens.radiusFull.value()}")
                property("transition", FADE_BACKGROUND_AND_BORDER)
            }
    }
    hover { Modifier.styleModifier { property("border-color", Tokens.accent.value().toString()) } }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    // Silk puts the children in a row of its own, so the gap has to go there or the glyph
    // ends up glued to the first letter.
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${Tokens.spaceSm.value()}")
        }
    }
}

/** The rail's `+` needs somewhere to open, and the rail itself must not clip it. */
val BriefStartHostStyle = CssStyle.base {
    Modifier.styleModifier { property("position", "relative") }
}

/** The little menu that decides how a session starts. */
val BriefStartMenuStyle = CssStyle.base {
    Modifier
        .backgroundColor(Tokens.surface.value())
        .styleModifier {
            property("position", "absolute")
            property("top", "0")
            property("left", "calc(100% + ${Tokens.spaceSm.value()})")
            property("z-index", "${Layer.Popover}")
            property("width", "232px")
            property("padding", "${Tokens.spaceXs.value()}")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "${Tokens.spaceXxs.value()}")
            property("border", "1px solid ${Tokens.line.value()}")
            property("border-radius", "${Tokens.radiusMd.value()}")
            property("box-shadow", "0 16px 40px rgba(0, 0, 0, 0.32)")
        }
}

/** One of its two options: a name and the line that says what it costs you. */
val BriefStartItemStyle = CssStyle {
    base {
        Modifier
            .color(Tokens.fg.value())
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "flex-start")
                property("gap", "2px")
                property("width", "100%")
                property("height", "auto")
                property("padding", "9px 11px")
                property("text-align", "left")
                property("font-family", "inherit")
                property("cursor", "pointer")
                property("background", "transparent")
                property("border", "1px solid transparent")
                property("border-radius", "${Tokens.radiusSm.value()}")
                property("transition", FADE_BACKGROUND_AND_BORDER)
            }
    }
    hover { Modifier.styleModifier { property("background", Tokens.raised.value().toString()) } }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    cssRule(" > *") {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
            property("gap", "2px")
            property("width", "100%")
            property("white-space", "normal")
        }
    }
}

/** The close control: quiet until you go near it, like every other icon-only button here. */
val BriefCloseStyle = CssStyle {
    base {
        Modifier.color(Tokens.muted.value()).styleModifier {
            property("width", "${Tokens.controlSm.value()}")
            property("height", "${Tokens.controlSm.value()}")
            property("min-width", "${Tokens.controlSm.value()}")
            property("flex", "none")
            property("padding", "0")
            property("background", "transparent")
            property("border", "1px solid transparent")
            property("border-radius", "${Tokens.radiusSm.value()}")
            property("cursor", "pointer")
            property("transition", FADE_BACKGROUND_AND_BORDER)
        }
    }
    hover {
        Modifier.color(Tokens.fg.value()).styleModifier { property("background", Tokens.raised.value().toString()) }
    }
    focusVisible { Modifier.styleModifier { property("box-shadow", "0 0 0 3px ${Tokens.focusRing.value()}") } }
    // Last in a head that wraps, it took a third line to itself and left a band of nothing. Out
    // of the flow it goes back to the corner everyone looks for it in; the padding the head
    // reserves at this width is what keeps the lede from running underneath it.
    until(Breakpoints.Md) {
        Modifier.styleModifier {
            property("position", "absolute")
            property("top", "calc(${Tokens.spaceLg.value()} + $SAFE_AREA_TOP)")
            property("right", "${Tokens.spaceLg.value()}")
        }
    }
}
